'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface OrderStage {
  id: string;
  label: string;
  description: string;
  timestamp: string | null;
  status: 'completed' | 'active' | 'pending';
  icon: string;
}

interface OrderStatusTimelineProps {
  orderId: string;
  currentStage: number;
}

const OrderStatusTimeline = ({ orderId, currentStage }: OrderStatusTimelineProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [stages, setStages] = useState<OrderStage[]>([
    {
      id: '1',
      label: 'Order Confirmed',
      description: 'Your order has been received and confirmed',
      timestamp: '2025-12-25 10:30 AM',
      status: 'completed',
      icon: 'CheckCircleIcon',
    },
    {
      id: '2',
      label: 'Packed',
      description: 'Your order is being carefully packed',
      timestamp: '2025-12-25 11:15 AM',
      status: 'completed',
      icon: 'CubeIcon',
    },
    {
      id: '3',
      label: 'Out for Delivery',
      description: 'Your order is on the way',
      timestamp: '2025-12-25 02:45 PM',
      status: 'active',
      icon: 'TruckIcon',
    },
    {
      id: '4',
      label: 'Delivered',
      description: 'Order delivered successfully',
      timestamp: null,
      status: 'pending',
      icon: 'HomeIcon',
    },
  ]);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  if (!isHydrated) {
    return (
      <div className="bg-card rounded-lg shadow-elevation-md p-6">
        <div className="animate-pulse space-y-6">
          <div className="h-6 bg-muted rounded w-1/3"></div>
          <div className="space-y-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="flex gap-4">
                <div className="w-12 h-12 bg-muted rounded-full"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-muted rounded w-1/4"></div>
                  <div className="h-3 bg-muted rounded w-1/2"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg shadow-elevation-md p-6">
      <h2 className="font-heading text-xl font-semibold text-foreground mb-6">
        Order Status
      </h2>

      {/* Desktop Timeline */}
      <div className="hidden md:block">
        <div className="relative">
          {/* Progress Line */}
          <div className="absolute top-6 left-6 right-6 h-0.5 bg-border">
            <div
              className="h-full bg-primary transition-all duration-500"
              style={{ width: `${(currentStage / (stages.length - 1)) * 100}%` }}
            ></div>
          </div>

          {/* Stages */}
          <div className="relative flex justify-between">
            {stages.map((stage, index) => (
              <div key={stage.id} className="flex flex-col items-center" style={{ width: '25%' }}>
                <div
                  className={`w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 ${
                    stage.status === 'completed'
                      ? 'bg-primary text-primary-foreground shadow-elevation-md'
                      : stage.status === 'active' ?'bg-accent text-accent-foreground shadow-elevation-md animate-pulse-subtle' :'bg-muted text-muted-foreground'
                  }`}
                >
                  <Icon name={stage.icon as any} size={24} />
                </div>
                <div className="mt-4 text-center">
                  <p
                    className={`font-medium text-sm ${
                      stage.status === 'pending' ? 'text-muted-foreground' : 'text-foreground'
                    }`}
                  >
                    {stage.label}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">{stage.description}</p>
                  {stage.timestamp && (
                    <p className="font-data text-xs text-primary mt-2">{stage.timestamp}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Mobile Timeline */}
      <div className="md:hidden space-y-4">
        {stages.map((stage, index) => (
          <div key={stage.id} className="flex gap-4">
            <div className="flex flex-col items-center">
              <div
                className={`w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 ${
                  stage.status === 'completed'
                    ? 'bg-primary text-primary-foreground shadow-elevation-md'
                    : stage.status === 'active' ?'bg-accent text-accent-foreground shadow-elevation-md animate-pulse-subtle' :'bg-muted text-muted-foreground'
                }`}
              >
                <Icon name={stage.icon as any} size={24} />
              </div>
              {index < stages.length - 1 && (
                <div
                  className={`w-0.5 flex-1 mt-2 ${
                    stage.status === 'completed' ? 'bg-primary' : 'bg-border'
                  }`}
                  style={{ minHeight: '40px' }}
                ></div>
              )}
            </div>
            <div className="flex-1 pb-6">
              <p
                className={`font-medium text-sm ${
                  stage.status === 'pending' ? 'text-muted-foreground' : 'text-foreground'
                }`}
              >
                {stage.label}
              </p>
              <p className="text-xs text-muted-foreground mt-1">{stage.description}</p>
              {stage.timestamp && (
                <p className="font-data text-xs text-primary mt-2">{stage.timestamp}</p>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default OrderStatusTimeline;